import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, Users, Mic, Trophy, ArrowRight, Loader2 } from "lucide-react";
import PageHead from "@/components/PageHead";
import { CountUp, MatchRing } from "@/components/ui-bits";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const PIPELINE_COLUMNS = ["Applied", "Screening", "Interview", "Offer"];

export default function RecruiterDashboard() {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [pipelineData, setPipelineData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchDashboardData = async () => {
      try {
        const [candRes, pipeRes] = await Promise.all([
          api.get("/recruiter/candidates"),
          api.get("/recruiter/pipeline"),
        ]);
        if (active) {
          setCandidates(candRes.data?.candidates || []);
          setPipelineData(pipeRes.data?.pipeline || {});
        }
      } catch (_) {
        if (active) {
          setCandidates([]);
          setPipelineData({});
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchDashboardData();
    return () => {
      active = false;
    };
  }, []);

  const totalCandidates = candidates.length;
  const inInterview = (pipelineData["Interview"] || []).length;
  const offersMade = (pipelineData["Offer"] || []).length;

  const STATS = [
    { label: "Active candidates", value: totalCandidates, icon: Users, color: "text-violet" },
    { label: "Screening & Review", value: (pipelineData["Screening"] || []).length, icon: Briefcase, color: "text-electric" },
    { label: "Live interviews", value: inInterview, icon: Mic, color: "text-aqua" },
    { label: "Offers extended", value: offersMade, icon: Trophy, color: "text-lime" },
  ];

  return (
    <div>
      <PageHead
        label={`${user?.profile?.company || "Recruiter"} workspace`}
        title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`}
        sub="Source, match and move verified talent through your pipeline."
      >
        <Link to="/recruiter/candidates" className="cv-btn-primary text-sm flex items-center gap-1.5">
          Find Talent <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHead>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {STATS.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="card-surface p-5 shadow-sm"
            >
              <Icon className={`h-5 w-5 ${s.color}`} />
              <p className="mt-3 font-heading text-3xl font-black">
                <CountUp to={s.value} />
              </p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold">Top matched candidates</h2>
            <Link to="/recruiter/candidates" className="text-sm font-semibold text-electric">
              View all
            </Link>
          </div>

          {loading ? (
            <div className="flex min-h-[160px] items-center justify-center rounded-2xl border border-dashed border-border">
              <Loader2 className="h-5 w-5 animate-spin text-electric" />
              <span className="ml-2 text-xs text-muted-foreground">Loading candidates…</span>
            </div>
          ) : candidates.length > 0 ? (
            <div className="space-y-3">
              {candidates.slice(0, 4).map((c) => (
                <Link
                  key={c.id}
                  to={`/recruiter/candidates/${c.id}`}
                  className="card-surface flex items-center gap-4 p-4 transition-transform hover:-translate-y-0.5 shadow-xs"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-electric to-violet font-heading text-base font-bold text-white shadow-xs">
                    {(c.name || "C").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {c.role} · {c.location}
                    </p>
                  </div>
                  <MatchRing value={c.match || 88} size={44} stroke={5} color="hsl(var(--chart-4))" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
              No registered candidates yet. Candidates will appear as job seekers create Career Profiles.
            </div>
          )}
        </div>

        <div className="card-surface p-6 shadow-sm flex flex-col justify-between">
          <div>
            <p className="label-caps mb-4 text-violet">Pipeline snapshot</p>
            <div className="space-y-3">
              {PIPELINE_COLUMNS.map((col) => {
                const count = (pipelineData[col] || []).length;
                return (
                  <div key={col} className="flex items-center justify-between">
                    <span className="text-xs font-semibold">{col}</span>
                    <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-bold border border-border">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <Link
            to="/recruiter/pipeline"
            className="cv-btn-ghost mt-6 w-full border border-border text-xs justify-center"
          >
            Open Full Pipeline
          </Link>
        </div>
      </div>
    </div>
  );
}
